import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Save, Trash2, ShieldAlert } from "lucide-react";
import { api, formatApiError } from "@/lib/api";

const PACKING_GROUPS = [{ v: 1, l: "Pack group I" }, { v: 2, l: "Pack group II" }, { v: 3, l: "Pack group III" }, { v: 4, l: "Pack group N/A" }];
const CONTAINERS = [{ v: 2, l: "Carton" }, { v: 1, l: "Drum" }, { v: 11, l: "Bag" }, { v: 3, l: "IBC" }, { v: 4, l: "Pail" }];
const CLASSES = [{ v: 15, l: "Class 9 — Miscellaneous (lithium batteries)" }, { v: 14, l: "Class 8 — Corrosive" }, { v: 5, l: "Class 3 — Flammable liquid" }];

const blank = { un_number: "", packing_group: 4, container_type: 2, dg_class_type: 15, aggregate_quantity_kg: "", containers_per_package: 1, enabled: true, note: "" };

export const DangerousGoods = ({ products }) => {
  const [saved, setSaved] = useState({});
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/machship/dg-items").then(({ data }) => {
    const map = {};
    (data.items || []).forEach((item) => { map[item.item_id] = item; });
    setSaved(map);
  }).catch((e) => toast.error(formatApiError(e, "Could not load dangerous goods"))).finally(() => setLoading(false));

  useEffect(() => { load(); }, []);

  const draftFor = (id) => drafts[id] || { ...blank, ...(saved[id] || {}) };
  const setField = (id, field, value) => setDrafts((d) => ({ ...d, [id]: { ...draftFor(id), [field]: value } }));

  const save = async (id) => {
    const draft = draftFor(id);
    if (!draft.un_number) return toast.error("Enter the UN number Gem gave you");
    setBusy(id);
    try {
      await api.put("/machship/dg-items", {
        item_id: id,
        un_number: Number(draft.un_number),
        packing_group: Number(draft.packing_group),
        container_type: Number(draft.container_type),
        dg_class_type: draft.dg_class_type ? Number(draft.dg_class_type) : null,
        aggregate_quantity_kg: draft.aggregate_quantity_kg ? Number(draft.aggregate_quantity_kg) : null,
        containers_per_package: draft.containers_per_package ? Number(draft.containers_per_package) : null,
        enabled: !!draft.enabled,
        note: draft.note || "",
      });
      toast.success("Saved — Gem now prices the real dangerous-goods rate for this product");
      setDrafts((d) => { const next = { ...d }; delete next[id]; return next; });
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not save"));
    } finally {
      setBusy("");
    }
  };

  const remove = async (id) => {
    setBusy(id);
    try {
      await api.delete(`/machship/dg-items/${id}`);
      toast.success("Removed — this product falls back to the interim allowance");
      load();
    } catch (err) {
      toast.error(formatApiError(err, "Could not remove"));
    } finally {
      setBusy("");
    }
  };

  const inputCls = "rounded-sm border border-ei-mist px-3 py-2 text-sm";

  if (loading) return <div className="mt-6 flex items-center gap-2 text-sm text-ei-graphite/70"><Loader2 className="w-4 h-4 animate-spin" /> Loading dangerous goods…</div>;

  return (
    <div className="mt-6 grid gap-4" data-testid="dg-settings">
      <div className="rounded-md border border-amber-200 bg-amber-50 p-4 text-sm text-amber-950">
        <p className="flex items-center gap-2 font-semibold"><ShieldAlert className="w-4 h-4" /> Until a product has its UN number here, battery freight carries the interim allowance of $50 per battery capped at $150.</p>
        <p className="mt-1 text-xs">Save the UN number and pack group Gem give you and the real carrier dangerous-goods rate is used instead, from the very next quote.</p>
      </div>
      {products.map((product) => {
        const draft = draftFor(product.id);
        const isSaved = !!saved[product.id];
        return (
          <div key={product.id} className="bg-white border border-ei-mist rounded-md p-4 grid gap-3" data-testid={`dg-row-${product.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-semibold text-ei-navy text-sm">{product.name}</p>
                <p className="text-xs text-ei-graphite/60">{product.id}</p>
              </div>
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${isSaved ? "bg-emerald-50 text-emerald-800" : "bg-amber-50 text-amber-800"}`} data-testid={`dg-state-${product.id}`}>
                {isSaved ? "Declared to Gem" : "Interim allowance"}
              </span>
            </div>
            <div className="grid gap-2 sm:grid-cols-3">
              <label className="grid gap-1 text-xs font-semibold text-ei-graphite/70">UN number
                <input type="number" value={draft.un_number} onChange={(e) => setField(product.id, "un_number", e.target.value)} placeholder="UN number from Gem" className={`${inputCls} tnum`} data-testid={`dg-un-${product.id}`} />
              </label>
              <label className="grid gap-1 text-xs font-semibold text-ei-graphite/70">Packing group
                <select value={draft.packing_group} onChange={(e) => setField(product.id, "packing_group", e.target.value)} className={inputCls} data-testid={`dg-pg-${product.id}`}>
                  {PACKING_GROUPS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </label>
              <label className="grid gap-1 text-xs font-semibold text-ei-graphite/70">Class
                <select value={draft.dg_class_type || ""} onChange={(e) => setField(product.id, "dg_class_type", e.target.value)} className={inputCls} data-testid={`dg-class-${product.id}`}>
                  <option value="">Not stated</option>
                  {CLASSES.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </label>
              <label className="grid gap-1 text-xs font-semibold text-ei-graphite/70">Inner container
                <select value={draft.container_type} onChange={(e) => setField(product.id, "container_type", e.target.value)} className={inputCls} data-testid={`dg-container-${product.id}`}>
                  {CONTAINERS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                </select>
              </label>
              <label className="grid gap-1 text-xs font-semibold text-ei-graphite/70">Battery weight per carton (kg)
                <input type="number" step="0.1" value={draft.aggregate_quantity_kg || ""} onChange={(e) => setField(product.id, "aggregate_quantity_kg", e.target.value)} placeholder="Gross carton weight if blank" className={`${inputCls} tnum`} data-testid={`dg-kg-${product.id}`} />
              </label>
              <label className="grid gap-1 text-xs font-semibold text-ei-graphite/70">Batteries per carton
                <input type="number" min="1" value={draft.containers_per_package || 1} onChange={(e) => setField(product.id, "containers_per_package", e.target.value)} className={`${inputCls} tnum`} data-testid={`dg-count-${product.id}`} />
              </label>
            </div>
            <input value={draft.note || ""} onChange={(e) => setField(product.id, "note", e.target.value)} placeholder="Note — who confirmed this and when" className={inputCls} data-testid={`dg-note-${product.id}`} />
            <div className="flex flex-wrap items-center gap-2">
              <button type="button" onClick={() => save(product.id)} disabled={busy === product.id} className="inline-flex items-center gap-2 font-display text-sm font-bold bg-ei-navy text-ei-white px-4 py-2.5 rounded-sm hover:bg-ei-ink min-h-[44px] disabled:opacity-60" data-testid={`dg-save-${product.id}`}>
                {busy === product.id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
              </button>
              {isSaved && (
                <button type="button" onClick={() => remove(product.id)} disabled={busy === product.id} className="inline-flex items-center gap-2 text-sm font-semibold text-red-700 px-3 py-2.5 rounded-sm hover:bg-red-50 min-h-[44px]" data-testid={`dg-remove-${product.id}`}>
                  <Trash2 className="w-4 h-4" /> Remove
                </button>
              )}
              {isSaved && saved[product.id].updated_by && (
                <span className="text-xs text-ei-graphite/60">Last saved by {saved[product.id].updated_by}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
};
